import * as Yup from "yup";
import LockIcon from "@material-ui/icons/Lock";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import BaseInput from "../../../../components/elements/BaseInput";
import Button from "../../../../components/elements/Button";
import FormError from "../../../../components/elements/FormError";
import FormFieldError from "../../../../components/elements/FormFieldError";
import classes from "./NewPasswordForm.module.scss";
import { successToaster } from "../../../../helpers/toasters";

type FormInitialValues = {
  confirmationCode: string;
  newPassword: string;
  newPasswordConfirm: string;
};

type NewPasswordFormProps = {
  userName: string;
};

const NewPasswordForm: FC<NewPasswordFormProps> = ({ userName }) => {
  const formInitialValues: FormInitialValues = {
    confirmationCode: "",
    newPassword: "",
    newPasswordConfirm: "",
  };

  const validationSchema = Yup.object({
    confirmationCode: Yup.string().required("Required field."),
    newPassword: Yup.string().required("Required field.").min(8, "Password is too short - should be 8 chars minimum."),
    newPasswordConfirm: Yup.string().oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  const history = useHistory();

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { confirmationCode, newPassword } = values;
      setIsLoading(true);
      await Auth.forgotPasswordSubmit(userName, confirmationCode, newPassword);
      setIsLoading(false);
      actions.resetForm();
      successToaster("Password successfully changed!");
      history.push("/");
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (errorMessage) {
        setErrorMessage("");
      }
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values }) => (
        <Form className={classes.container}>
          <div className={classes.formContent}>
            <div className={classes.formHeader}>
              <Typography variant="h3">New Password</Typography>
              <LockIcon className={classes.icon} />
            </div>

            <Typography className={classes.infoText}>
              Please enter the confirmation code sent to your email in the last step and the new password.
            </Typography>

            <div className={classes.inputs}>
              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.confirmationCode}
                  error={!!errorMessage}
                  name="confirmationCode"
                  variant="outlined"
                  label="Confirmation code *"
                  onChange={handleFieldChange(handleChange("confirmationCode"))}
                />

                <FormFieldError name="confirmationCode" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.newPassword}
                  error={!!errorMessage}
                  name="newPassword"
                  variant="outlined"
                  label="New Password *"
                  type="password"
                  onChange={handleFieldChange(handleChange("newPassword"))}
                />

                <FormFieldError name="newPassword" />
              </div>

              <div className={classes.input}>
                <Field
                  as={BaseInput}
                  value={values.newPasswordConfirm}
                  error={!!errorMessage}
                  name="newPasswordConfirm"
                  variant="outlined"
                  label="Confirm new password *"
                  type="password"
                  onChange={handleFieldChange(handleChange("newPasswordConfirm"))}
                />

                <FormFieldError name="newPasswordConfirm" />
                <FormError className={classes.formError} errorMessage={errorMessage} />
              </div>
            </div>

            <div className={classes.formControls}>
              <Button variant="contained" type="submit" isLoading={isLoading} fullWidth>
                Send
              </Button>

              <AppLink highlighted to="/request-password-reset">
                Cancel
              </AppLink>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default NewPasswordForm;
